import { Component, DestroyRef, OnInit, ViewChild, WritableSignal, inject, input, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IErrorMessage, IOption } from '../../../../core/types/general';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TooltipDirective, TooltipPosition } from '../../../../core/directives/tooltip';
import { combineLatest, debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AppTranslateService } from '../../../../core/services/app.translate.service';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { isOptionTruncated } from '../../../../core/helpers/isTextTruncated';

@Component({
  selector: 'app-select',
  imports: [
    MatSelectModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    NgClass,
    TooltipDirective,
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
})
export class SelectComponent implements OnInit {
  protected readonly appTranslate = inject(AppTranslateService);

  className = input.required<string>();
  selectControl = input.required<FormControl>();
  label = input<string>();
  placeholder = input<string>();
  options = input.required<IOption[]>();
  errorMessages = input<IErrorMessage[]>([]);
  hintMessage = input<string | null>();
  readonly = input<boolean>(false);
  hideRequiredMarker = input<boolean>(false);
  subscriptSizing = input<'fixed' | 'dynamic'>('fixed');
  infoTooltip = input<string | null>('');
  selectChangesEvent = output<MatSelectChange>();
  optionTruncationConfig: WritableSignal<Record<string, boolean>> = signal({});
  protected readonly Validators = Validators;
  searchConfig = input<{ placeholder: string } | null>(null);
  searchControl = new FormControl('');
  filteredOptions: WritableSignal<IOption[]> = signal([]);
  private destroyRef = inject(DestroyRef);
  iconClickEvent = output<IOption>();

  protected readonly TooltipPosition = TooltipPosition;

  @ViewChild('customSelect') matSelect!: MatSelect;
  constructor() {
    this.checkSearchChanges();
  }

  ngOnInit(): void {
    this.initTruncationConfig();
  }

  onOpenChange(event: boolean) {
    if (this.readonly()) {
      this.matSelect.close();
      return;
    }
    if (event) {
      this.checkOptionsTruncation();
    } else {
      this.clearSearch();
    }
  }

  private initTruncationConfig(): void {
    this.optionTruncationConfig.update((initialValue) => {
      const result = { ...initialValue };
      this.options().forEach((option) => {
        const key = this.className() + '_' + option.id;
        result[key] = false;
      });
      return result;
    });
  }

  private checkOptionsTruncation(): void {
    setTimeout(() => {
      this.optionTruncationConfig.update((value) => {
        const result = { ...value };
        Object.keys(value).forEach((optionHtmlId) => {
          result[optionHtmlId] = isOptionTruncated(optionHtmlId);
        });
        return result;
      });
    });
  }

  private clearSearch(): void {
    this.searchControl.reset();
  }

  private checkSearchChanges(): void {
    combineLatest([toObservable(this.options), this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged())])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: [IOption[], string | null]) => {
        if (value[1]) {
          const optionsModified = value[0].filter((option) =>
            option?.name
              ? option.name
                  .toLowerCase()
                  .trim()
                  .includes(value[1] || '')
              : ''
          );
          this.filteredOptions.set(optionsModified);
        } else {
          this.filteredOptions.set(value[0]);
        }
      });
    this.searchControl.setValue('');
  }

  emitClick(event: MouseEvent, option: IOption): void {
    event.stopPropagation();
    this.iconClickEvent.emit(option);
  }

  getFirstError() {
    return this.errorMessages().find((e) => this.selectControl().hasError(e.errorName) && this.selectControl().touched);
  }

  closePanel() {
    this.matSelect.close();
  }
}
